import React, { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';
import qs from 'qs';
import { getActiveLanguage } from 'react-localize-redux';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Auth0CallbackPath } from '../../../constants/auth0.constants';
import { generateString } from '../../../helpers/utilities';
import { accountService } from '../../../services/account.service';
import {
  canUseIdTokenCookie,
  isAuth0IdTokenCutoverEnabled,
  loginWithIdTokenRedirect,
} from '../../../services/auth/idToken/idToken.service';
import Loading from '../../Loading';
import { storePostLoginRedirect } from './utils';

// ID token cutover: starts the legacy code-exchange login. Kept byte-for-byte identical
// to the pre-cutover redirect; used when the cutover is off, and as the fail-safe when
// the SDK redirect cannot start.
function startClassicLogin(
  auth0Domain: string | undefined,
  auth0ClientId: string | undefined,
  redirectUri: string,
  languageCode: string,
  email: string | null,
  screenHint: string | null
): void {
  // Generate random string for state in oauth flow. This should be checked in the callback for additional security.
  const state = generateString(16);
  sessionStorage.removeItem('fdauth0state');
  sessionStorage.setItem('fdauth0state', state);

  const query: { [key: string]: string | null | undefined } = {
    response_type: 'code',
    client_id: auth0ClientId,
    redirect_uri: redirectUri,
    scope: 'openid profile email offline_access',
    login_hint: email,
    screen_hint: screenHint,
    state,
    ui_locales: languageCode,
  };

  window.location.href = `https://${auth0Domain}/authorize?${qs.stringify(query)}`;
}

const Auth0Login = (props: MappedState) => {
  const { languageCode } = props;
  const redirectUri = window.location.origin.toString() + Auth0CallbackPath;

  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const email = params.get('email');
  const screenHint = params.get('screen_hint');

  const { data, isPending, isError } = useQuery({
    queryKey: ['auth0-config'],
    queryFn: () => accountService.getAuth0Config(),
  });

  useEffect(() => {
    const postLoginRedirect = params.get('redirectUrl');
    storePostLoginRedirect(postLoginRedirect);
  }, [params]);

  useEffect(() => {
    if (isPending || isError || !data) {
      return;
    }

    if (isAuth0IdTokenCutoverEnabled() && canUseIdTokenCookie()) {
      // ID token cutover: the SDK manages state, PKCE and the redirect itself; the
      // callback then skips the Zeus exchange. If the redirect cannot start (missing
      // SPA client id, SDK failure), fail safe into the classic flow. Origins where the
      // ID-token cookie cannot function (local development, unknown hosting) also use
      // the classic flow — the new login would otherwise produce a credential no API
      // call can carry.
      loginWithIdTokenRedirect({
        // Feature parity with startClassicLogin below: prefill the email, route new
        // invitees to the signup screen and localise the hosted page.
        login_hint: email ?? undefined,
        screen_hint: screenHint ?? undefined,
        ui_locales: languageCode,
      }).catch((error: unknown) => {
        console.error(
          '[IdToken] SDK login redirect failed; falling back to the classic flow.',
          error
        );
        startClassicLogin(
          data.Auth0Domain,
          data.Auth0ClientId,
          redirectUri,
          languageCode,
          email,
          screenHint
        );
      });
      return;
    }

    startClassicLogin(
      data.Auth0Domain,
      data.Auth0ClientId,
      redirectUri,
      languageCode,
      email,
      screenHint
    );
  }, [data, isPending, isError, redirectUri, email, screenHint]);

  return <Loading fullscreen />;
};

type MappedState = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState) => {
  return {
    languageCode: getActiveLanguage(state.locale),
  };
};

export default connect(mapStateToProps)(Auth0Login);
